import { Router } from '@angular/router';
import { SsoStrategyService } from './sso-strategy.service';
import { OauthService } from './oauth.service';
import { environment } from 'src/environments/environment';
import { UrlSettingsCore } from './core/configuration/url-settings-core';
import { DialogService } from './core/components/dialog/dialog.service';
import { AppData } from './data-store';
import { HelpersService } from './core/services/helpers.service';
import { UserManager } from './core/services/user-manager';

export function appInitializer(
    ssoStrategy: SsoStrategyService, 
    router: Router, 
    oauthService: OauthService,
    helpers: HelpersService,
    userManager: UserManager,
    dialog: DialogService
): () => Promise<void> {
    return async () => {
        await ssoStrategy.loadAuthConfig();
        const mode = ssoStrategy.getAuthMode();
        
        if (mode === 'ldap') {
          helpers.getLoggedUserData().subscribe((response) => {
            if (response) {
              userManager.sendUserLogged(true);
            } else {
              router.navigate([UrlSettingsCore.URL_LOGIN]);
            }
          });
        } else if (mode === "oauth2") {
            const queryString = window.location.search;
            const urlParams = new URLSearchParams(queryString);
            const code = urlParams.get('code');
            let idpHint = null;
            if(environment.local) {
              idpHint = "tnt_"+(environment.tenant ? environment.tenant : "napoli");
            } else {
              idpHint = getIdpHint();
            }
            AppData.getInstance().idpHint = idpHint;

            // Fix #6: rimosso await inutile, localStorage.getItem è sincrono
            const token = localStorage.getItem("access_token");

            // caso in cui ho un token ma esso non è valido
            if(!!token && !oauthService.hasValidToken()) {
              if(oauthService.isTokenIdValid()) {
                oauthService.invalidateTokenSession();
              } else {
                // nel caso in cui id token hint fosse nullo o invalido bypasso la logout
                // pulisco la sessione e accedo nuovamente
                oauthService.clearOAuthData();
                oauthService.redirectToKeycloak(idpHint);
              }
              return;
            }
            // caso in cui ho un token valido oppure un code
            // in caso contrario ricomincio nuovamente il processo di login
            if (oauthService.hasValidToken() || code) {
                if(oauthService.hasValidToken()) {
                    getAccessTokenOnInit();
                    return;
                }
                if(!!code) {
                    fetchAccessToken(code);
                }
            } else {
                if(!idpHint) {
                    ssoStrategy.onForbidden(true);
                    router.navigate([UrlSettingsCore.URL_FORBIDDEN]);
                    return;
                }
                oauthService.clearOAuthData();
                oauthService.redirectToKeycloak(idpHint);
            }
        }
    };

    // Fix #2: aggiunto error handler — se il refresh fallisce all'avvio
    // pulisce la sessione e rimanda a Keycloak invece di bloccarsi in loading infinito
    function getAccessTokenOnInit() {
        oauthService.refreshAccessToken().subscribe({
          next: res => {
            localStorage.setItem("access_token", res.access_token);
            localStorage.setItem("refresh_token", res.refresh_token);
            localStorage.setItem("expires_in", res.expires_in.toString());
            localStorage.setItem("refresh_expires_in", res.refresh_expires_in.toString());
            localStorage.setItem("session_state", res.session_state);
            localStorage.setItem("id_token", res.id_token);
            oauthService.onAccessTokenObtained(true);
          },
          error: err => {
            oauthService.clearOAuthData();
            oauthService.redirectToKeycloak(AppData.getInstance().idpHint);
          }
        });
    }

    function getIdpHint() {
        let tenant = localStorage.getItem("OAUTH_TENANT");
        const parts = window.location.pathname.split('/').filter(part => part !== "");
        let idpHint = parts.length > 0 ? `tnt_${parts[0]}` : null;
        if(!!tenant && !idpHint?.includes(tenant)) {
          AppData.getInstance().TNT_TENANT = tenant;
          localStorage.removeItem("access_token");
          return null;
        }
        AppData.getInstance().TNT_TENANT = parts[0];
        return idpHint;
    }

    function fetchAccessToken(code: string) {
        oauthService.getAccessToken(code).subscribe(
          res => {
            let tokenCodified = JSON.parse(atob(res.access_token.split('.')[1]));
            localStorage.setItem("access_token", res.access_token);
            localStorage.setItem("refresh_token", res.refresh_token);
            localStorage.setItem("expires_in", res.expires_in.toString());
            localStorage.setItem("refresh_expires_in", res.refresh_expires_in.toString());
            localStorage.setItem("session_state", res.session_state);
            localStorage.setItem("id_token", res.id_token);
            let hasGrantAccessRole = tokenCodified?.realm_access.roles.indexOf("TELEFORUM_ACCESS") > -1;
            if(hasGrantAccessRole) {
              localStorage.setItem("TLF_USER", tokenCodified.preferred_username);
              let params = new URLSearchParams(location.search);
              params.delete('code');
              params.delete('iss');
              params.delete('session_state');
              history.replaceState(null, '', window.location.href.split("?")[0] + params + location.hash);
              oauthService.onAccessTokenObtained(true);
              router.navigate([UrlSettingsCore.URL_VISUALIZZA_WELCOME_BOARD]);
            } else {
              oauthService.invalidateTokenSession();
            }
          }
        );
    }
}